import type { Theme as MuiTheme } from '@mui/material';
import { type Components, darken, lighten } from '@mui/material/styles';

import type {} from '@mui/lab/themeAugmentation';

export function createComponents(baseTheme: MuiTheme): Components {
  return {
    MuiButton: {
      defaultProps: {
        variant: 'contained',
      },
      styleOverrides: {
        root: {
          fontWeight: 500,
          borderRadius: '4px',
          textTransform: 'none',
          variants: [
            {
              props: { color: 'neutral', variant: 'contained' },
              style: {
                backgroundColor: baseTheme.palette.neutral.dark,
                color: baseTheme.palette.neutral.contrastText,
                '&:hover': {
                  backgroundColor: lighten(baseTheme.palette.neutral.dark, 0.1),
                },
              },
            },
          ],
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        rounded: {
          borderRadius: baseTheme.spacing(1),
        },
      },
    },
    MuiMenu: {
      defaultProps: {
        // This is a workaround to prevent the menu from having issues with the parent element
        // due to the parent element having a transform style, and the menu offset being wrong.
        container: document.body,
        // The above will NOT work without this.
        disablePortal: false,
      },
    },
    MuiPopover: {
      defaultProps: {
        slotProps: {
          paper: {
            variant: 'outlined',
          },
        },
        // This is a workaround to prevent the menu from having issues with the parent element
        // due to the parent element having a transform style, and the menu offset being wrong.
        container: document.body,
        // The above will NOT work without this.
        disablePortal: false,
      },
    },
    MuiCard: {
      defaultProps: {
        elevation: 0,
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          borderColor: baseTheme.palette.divider,
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          paddingTop: baseTheme.spacing(1),
        },
      },
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          paddingLeft: baseTheme.spacing(2),
          paddingRight: baseTheme.spacing(2),
          paddingBottom: baseTheme.spacing(2),
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: baseTheme.palette.text.primary,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          variants: [
            {
              props: { shape: 'rounded' },
              style: {
                borderRadius: '6px',
              },
            },
            {
              props: { size: 'xsmall' },
              style: {
                fontSize: '0.75rem',
                height: '20px',
                paddingLeft: '4px',
                paddingRight: '4px',
                paddingTop: '2px',
                paddingBottom: '2px',
                '& .MuiChip-label': {
                  padding: 0,
                },
              },
            },
          ],
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'medium',
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          variants: [
            {
              props: { size: 'xsmall' },
              style: {
                '& .MuiOutlinedInput-root': {
                  padding: '4px 8px',
                  '& .MuiInputBase-input': {
                    paddingTop: '2px',
                    paddingBottom: '2px',
                    paddingLeft: '1px',
                    fontSize: '0.75rem',
                  },
                },
              },
            },
          ],
        },
      },
    },
  };
}

/*
|------------------
| Button
|------------------
*/

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    neutral: true;
  }
}

/*
|------------------
| TextField
|------------------
*/

export type TextFieldSize = 'xsmall' | 'small' | 'medium';
export type TextFieldVariant =
  | 'standard'
  | 'outlined'
  | 'filled'
  | 'borderless';

declare module '@mui/material/TextField' {
  interface TextFieldPropsSizeOverrides {
    xsmall: true;
  }
  interface BaseTextFieldProps {
    border?: string;
  }
}

/*
|------------------
| Chip
|------------------
*/

export type ChipShape = 'rounded' | 'circular';

declare module '@mui/material/Chip' {
  interface ChipOwnProps {
    shape?: ChipShape;
  }
  interface ChipPropsSizeOverrides {
    xsmall: true;
  }
}
